import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCompress,
  faDownload,
  faEye,
  faExpand,
  faFilePdf,
  faSpinner,
  faSyncAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../config";

const STATUS_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "UPLOADED", label: "Uploaded" },
  { value: "PROCESSING", label: "Processing" },
  { value: "COMPLETED", label: "Completed" },
  { value: "FAILED", label: "Failed" },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const statusClassName = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "FAILED") return "bg-rose-100 text-rose-700";
  if (normalized === "PROCESSING") return "bg-amber-100 text-amber-700";
  if (normalized === "COMPLETED") return "bg-emerald-100 text-emerald-700";
  return "bg-sky-100 text-sky-700";
};

const resolveErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (!message) return fallback;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.join(", ");
  if (typeof message === "object") return Object.values(message).flat().join(", ");
  return fallback;
};

const ClaimsRtpDownloadedFormHistory = () => {
  const token = localStorage.getItem("authentication/token");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [search, setSearch] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [providerCode, setProviderCode] = useState("");
  const [rtpCode, setRtpCode] = useState("ALL");
  const [generatedBy, setGeneratedBy] = useState("");
  const [status, setStatus] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reasonOptions, setReasonOptions] = useState([]);

  const [selectedDetails, setSelectedDetails] = useState(null);
  const [isDetailsMaximized, setIsDetailsMaximized] = useState(false);

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/claims/processing/rtp-form-history`, {
        params: {
          page,
          per_page: perPage,
          search: search.trim(),
          batch_no: batchNo.trim(),
          provider_code: providerCode.trim(),
          rtp_code: rtpCode === "ALL" ? "" : rtpCode,
          generated_by: generatedBy.trim(),
          status,
          date_from: dateFrom || "",
          date_to: dateTo || "",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data?.data || [];
      setRows(data);
      const pagination = response.data?.pagination || {};
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total || 0);
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to fetch RTP Form history."), {
        duration: 4000,
      });
      setRows([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    perPage,
    search,
    batchNo,
    providerCode,
    rtpCode,
    generatedBy,
    status,
    dateFrom,
    dateTo,
    token,
  ]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    let mounted = true;
    const fetchReasonOptions = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/claims/processing/rtp-entry/reasons`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!mounted) return;
        setReasonOptions(response.data?.data || []);
      } catch (error) {
        if (!mounted) return;
        setReasonOptions([]);
      }
    };

    fetchReasonOptions();
    return () => {
      mounted = false;
    };
  }, [token]);

  const openDetails = async (row) => {
    if (!row?.upload_id) return;
    const loadingToastId = toast.loading("Loading RTP downloaded form details...");
    try {
      setDetailsLoading(true);
      const response = await axios.get(`${BASE_URL}/claims/processing/rtp-form-history/details`, {
        params: { upload_id: row.upload_id },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSelectedDetails({
        header: response.data?.header || row,
        items: response.data?.items || [],
      });
      toast.success("RTP downloaded form details loaded.", { duration: 2200 });
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to load RTP Form details."), {
        duration: 4000,
      });
    } finally {
      setDetailsLoading(false);
      toast.dismiss(loadingToastId);
    }
  };

  const closeDetails = () => {
    setSelectedDetails(null);
    setDetailsLoading(false);
    setIsDetailsMaximized(false);
  };

  const refreshLink = async (row) => {
    if (!row?.upload_id) {
      toast.error("Missing upload ID for refresh.", { duration: 3000 });
      return;
    }

    const loadingToastId = toast.loading("Refreshing file link...");
    try {
      const response = await axios.post(
        `${BASE_URL}/claims/refresh-url`,
        { upload_id: row.upload_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const refreshedUrl =
        response.data?.signed_url || response.data?.file_url || response.data?.file?.signed_url;
      if (!refreshedUrl) {
        toast.error("Failed to refresh file link.", { duration: 3500 });
        return;
      }

      setRows((prev) =>
        prev.map((item) =>
          item.upload_id === row.upload_id
            ? { ...item, signed_url: refreshedUrl, url_expires_at: response.data?.expires_at || item.url_expires_at }
            : item,
        ),
      );
      setSelectedDetails((prev) => {
        if (!prev?.header) return prev;
        if (prev.header.upload_id !== row.upload_id) return prev;
        return {
          ...prev,
          header: {
            ...prev.header,
            signed_url: refreshedUrl,
            url_expires_at: response.data?.expires_at || prev.header.url_expires_at,
          },
        };
      });

      toast.success("Link refreshed successfully.", { duration: 3000 });
    } catch (error) {
      toast.error(resolveErrorMessage(error, "Failed to refresh file link."), {
        duration: 4000,
      });
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  const downloadFile = (row) => {
    if (!row?.signed_url) {
      toast.error("No downloadable link found. Please refresh link first.", { duration: 3500 });
      return;
    }
    window.open(row.signed_url, "_blank", "noopener,noreferrer");
    toast.success("Opening PDF download link.", { duration: 2200 });
  };

  const applyFilters = () => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      toast.error("Generated date range is invalid.", { duration: 3500 });
      return;
    }
    setPage(1);
    fetchRows();
  };

  const resetFilters = () => {
    setSearch("");
    setBatchNo("");
    setProviderCode("");
    setRtpCode("ALL");
    setGeneratedBy("");
    setStatus("ALL");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">For RTP Downloaded Form History</h1>
          <p className="text-sm text-gray-600">
            View generated RTP form PDFs with refresh and download actions.
          </p>
        </div>
        <button
          onClick={fetchRows}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
        >
          <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
          Refresh
        </button>
      </div>

      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <div>
            <label className="text-xs text-gray-600">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Batch #</label>
            <input
              value={batchNo}
              onChange={(e) => setBatchNo(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Batch no"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Provider Code</label>
            <input
              value={providerCode}
              onChange={(e) => setProviderCode(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Provider code"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">RTP Reason</label>
            <select
              value={rtpCode}
              onChange={(e) => setRtpCode(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="ALL">All RTP Reasons</option>
              {reasonOptions.map((reason) => (
                <option key={reason.rtp_code} value={reason.rtp_code}>
                  {reason.reasons} ({reason.rtp_code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Generated By</label>
            <input
              value={generatedBy}
              onChange={(e) => setGeneratedBy(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Generated by"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Generated Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Generated Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Search</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Upload ID / File / Batch / Reason / Provider"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Per Page</label>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={applyFilters} className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white">
            Apply Filters
          </button>
          <button onClick={resetFilters} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
            Reset
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between p-3 text-sm text-gray-600">
          <span>{showingText}</span>
          <span>RTP PDF Download History</span>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="p-3 text-left">Generated Date</th>
                <th className="p-3 text-left">Batch #</th>
                <th className="p-3 text-left">Provider</th>
                <th className="p-3 text-left">RTP Reason</th>
                <th className="p-3 text-left">Claim Count</th>
                <th className="p-3 text-left">Generated By</th>
                <th className="p-3 text-left">File</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Link Expiry</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="10" className="p-5 text-center text-gray-500">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Loading RTP Form history...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="p-5 text-center text-gray-500">
                    No RTP form history records found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.upload_id || row.id}>
                    <td className="p-3">{formatDateTime(row.created_at)}</td>
                    <td className="p-3">{row.batch_no || "-"}</td>
                    <td className="p-3">{row.provider_name || row.provider_code || "-"}</td>
                    <td className="p-3">
                      {row.rtp_reason || "-"} {row.rtp_code ? `(${row.rtp_code})` : ""}
                    </td>
                    <td className="p-3">{row.claim_count || 0}</td>
                    <td className="p-3">{row.generated_by_name || "-"}</td>
                    <td className="p-3">
                      <div className="max-w-xs truncate" title={row.original_file_name || ""}>
                        {row.original_file_name || "-"}
                      </div>
                      <div className="text-xs text-gray-500">{formatBytes(row.file_size)}</div>
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusClassName(
                          row.status,
                        )}`}
                      >
                        {String(row.status || "-").toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3">{formatDateTime(row.url_expires_at)}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openDetails(row)}
                          className="rounded-lg bg-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-700"
                        >
                          <FontAwesomeIcon icon={faEye} className="mr-1" />
                          View
                        </button>
                        <button
                          onClick={() => refreshLink(row)}
                          className="rounded-lg bg-sky-600 px-2 py-1 text-xs text-white hover:bg-sky-700"
                        >
                          <FontAwesomeIcon icon={faSyncAlt} className="mr-1" />
                          Refresh Link
                        </button>
                        <button
                          onClick={() => downloadFile(row)}
                          className="rounded-lg bg-emerald-600 px-2 py-1 text-xs text-white hover:bg-emerald-700"
                        >
                          <FontAwesomeIcon icon={faDownload} className="mr-1" />
                          Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3 text-sm text-gray-600">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page >= totalPages}
            className="rounded-md border border-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {selectedDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            className={`w-full rounded-xl bg-white p-6 shadow-lg transition-all ${
              isDetailsMaximized
                ? "max-w-[96vw] h-[94vh] overflow-y-auto"
                : "max-w-6xl max-h-[92vh] overflow-y-auto"
            }`}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">RTP Downloaded Form Details</h2>
                <p className="text-xs text-gray-500">
                  Upload ID: {selectedDetails?.header?.upload_id || "-"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsDetailsMaximized((prev) => !prev)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={isDetailsMaximized ? faCompress : faExpand} className="mr-1" />
                  {isDetailsMaximized ? "Original Size" : "Maximize"}
                </button>
                <button
                  onClick={closeDetails}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  Close
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Batch #</div>
                <div>{selectedDetails?.header?.batch_no || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Provider</div>
                <div>{selectedDetails?.header?.provider_name || selectedDetails?.header?.provider_code || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">RTP Reason</div>
                <div>
                  {selectedDetails?.header?.rtp_reason || "-"}{" "}
                  {selectedDetails?.header?.rtp_code ? `(${selectedDetails.header.rtp_code})` : ""}
                </div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Generated By</div>
                <div>{selectedDetails?.header?.generated_by_name || "-"}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Generated Date</div>
                <div>{formatDateTime(selectedDetails?.header?.created_at)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs text-gray-500">Claim Count</div>
                <div>{selectedDetails?.header?.claim_count || 0}</div>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 md:col-span-3">
                <div className="text-xs text-gray-500">Generated PDF</div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <FontAwesomeIcon icon={faFilePdf} className="mr-2 text-rose-600" />
                    {selectedDetails?.header?.original_file_name || "-"}
                  </div>
                  <button
                    onClick={() => refreshLink(selectedDetails?.header)}
                    className="rounded-lg bg-sky-600 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-700"
                  >
                    <FontAwesomeIcon icon={faSyncAlt} className="mr-1" />
                    Refresh Link
                  </button>
                  <button
                    onClick={() => downloadFile(selectedDetails?.header)}
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                  >
                    <FontAwesomeIcon icon={faDownload} className="mr-1" />
                    Download PDF
                  </button>
                  <div className="text-xs text-gray-500">
                    Expires: {formatDateTime(selectedDetails?.header?.url_expires_at)}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
              <div className="bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                Included Claims / LOA Rows
              </div>
              <div className="max-h-72 overflow-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-white text-slate-600">
                    <tr>
                      <th className="p-2 text-left">Transaction #</th>
                      <th className="p-2 text-left">LOA #</th>
                      <th className="p-2 text-left">Member</th>
                      <th className="p-2 text-left">Corporate</th>
                      <th className="p-2 text-left">Transaction Date</th>
                      <th className="p-2 text-left">Coverage</th>
                      <th className="p-2 text-left">Claim Status</th>
                      <th className="p-2 text-left">Claim Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {detailsLoading ? (
                      <tr>
                        <td colSpan="8" className="p-4 text-center text-slate-500">
                          <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                          Loading details...
                        </td>
                      </tr>
                    ) : (selectedDetails?.items || []).length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-4 text-center text-slate-500">
                          No rows found.
                        </td>
                      </tr>
                    ) : (
                      (selectedDetails?.items || []).map((item, idx) => (
                        <tr key={`${item.trans_no}-${idx}`}>
                          <td className="p-2">{item.trans_no || "-"}</td>
                          <td className="p-2">{item.loa_no || "-"}</td>
                          <td className="p-2">{item.member_name || "-"}</td>
                          <td className="p-2">{item.corporate_name || "-"}</td>
                          <td className="p-2">{formatDateTime(item.trans_date)}</td>
                          <td className="p-2">{item.coverage_code || "-"}</td>
                          <td className="p-2">{item.claim_status || "-"}</td>
                          <td className="p-2">
                            {Number(item.claim_amount || 0).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsRtpDownloadedFormHistory;
